import React, { useState, ReactElement, useRef, useEffect } from 'react'
import TabSelector from 'plugin-assets/shared/widget/components/tab-selector'
import styled, { css } from 'styled-components'
import theme from 'live/services/theme'

interface Props {
    type: string
    isEditor: boolean
    tabs: ReactElement[]
    changeConfig: (field: string, value: unknown, sideEffects?: Record<string, boolean>) => void
    widgetConfig: GridConfig
}

export default function Configs(props: Props): ReactElement | null {
    const [tab, changeTab] = useState('channels')
    const [elHeight, setElHeight] = useState(300)
    const [windowHeight, setWindowHeight] = useState(0)
    const containerRef = useRef(null)

    useEffect(() => {
        window.addEventListener('resize', () => setWindowHeight(window.innerHeight))

        return () => {
            window.removeEventListener('resize', () => setWindowHeight(window.innerHeight))
        }
    }, [])

    useEffect(() => {
        const paddingFromTheBottom = 15
        const newState = window.innerHeight - containerRef.current.getBoundingClientRect().y - paddingFromTheBottom
        setElHeight(newState)
    }, [windowHeight])

    if (props.tabs.length === 0) {
        return null
    }

    const current = tab === 'chart' && props.tabs.length > 1 ? props.tabs[1] : props.tabs[0]

    const Components = current ? current.type : () => null

    return (
        <Theme isEditor={props.isEditor} elHeight={elHeight} ref={containerRef}>
            {props.tabs.length > 1 ? <TabSelector value={tab} onChange={changeTab} /> : null}
            <StyledSelectContainer isEditor={props.isEditor}>
                <Components
                    {...current.props}
                    widgetConfig={props.widgetConfig}
                    changeConfig={props.changeConfig}
                    isEditor={props.isEditor}
                />
            </StyledSelectContainer>
        </Theme>
    )
}

export const Theme = styled.div<{ isEditor: boolean; elHeight; ref }>`
    display: flex;
    flex-direction: column;
    overflow: auto;
    border-bottom: ${({ isEditor }) => (isEditor ? '' : '1px solid rgb(40, 51, 74)')};
    max-height: ${({ isEditor, elHeight }) => isEditor && `${elHeight}px`};
    min-height: ${({ isEditor, elHeight }) => isEditor && `${elHeight > 400 ? 400 : elHeight}px`};
    .slider-input {
        width: 210px;
    }
    &::-webkit-scrollbar {
        width: 5px;
        background: '';
    }
    &::-webkit-scrollbar-track {
        background: '';
        border-radius: 0;
        margin: 1px;
    }
    &::-webkit-scrollbar-track-piece {
        background: '';
    }
    &::-webkit-scrollbar-thumb {
        background: rgb(186, 192, 203);
        border-radius: 0;
        height: 50px;
        background-clip: padding-box;
    }
`

const StyledSelectContainer = styled.div<{ isEditor: boolean }>`
    .input-field {
        display: flex;
        border-radius: 3px;

        > input {
            border-color: transparent;
        }
    }

    ${({ isEditor }) =>
        isEditor
            ? css`
                  .input-field {
                      border: 1px solid ${theme['light-borders']};
                  }
                  .Add-group-btn {
                      background-color: var(--gray-200);
                      border: 1px solid var(--gray-500);
                      border-radius: 1px;
                      margin-left: auto;
                      color: var(--gray-700);
                      &:hover {
                          background-color: var(--gray-300);
                          border-color: var(--gray-600);
                      }
                  }
              `
            : css`
                  h3 {
                      color: #fff;
                  }

                  hr {
                      border-color: 1px solid rgb(40, 51, 74);
                  }
                  .Add-group-btn {
                      background-color: ${theme['dark-bg']};
                      border: 1px solid ${theme['darker-bg']};
                      border-radius: 1px;
                      margin-left: auto;
                      color: white;
                      &:hover {
                          color: #bbb;
                      }
                  }

                  .input-config {
                      div {
                          border-color: ${theme['darker-bg']};
                          background-color: ${theme['text-color']};
                      }
                      .input-field {
                          border: 1px solid ${theme['darker-bg']};
                          background-color: ${theme['dark-bg']};
                          color: white;
                      }
                  }

                  .action-toogle > a {
                      background-color: ${theme['dark-bg']};
                  }

                  .input-field > input {
                      -webkit-box-shadow: none;
                      border-radius: 3px;
                      background-color: ${theme['dark-bg']};
                      color: ${theme.white} !important;
                  }
                  .Select-menu-outer {
                      border-color: ${theme['darker-bg']};
                  }
                  .Select-value {
                      background: ${theme['dark-bg']};

                      span.Select-value-label {
                          color: ${theme.white} !important;
                      }
                  }
                  .Select-input {
                      color: lightgray;
                  }
                  .Select-control,
                  .Select-placeholder {
                      background-color: ${theme['dark-bg']};
                  }
                  .Select-placeholder {
                      color: lightgray;
                  }
                  .Select-control {
                      border-radius: 3px;
                      background-color: ${theme['dark-bg']};
                      border: solid 1px ${theme['darker-bg']};
                  }
                  .Select.is-open > .Select-control,
                  .Select-control {
                      border-radius: 3px;
                      border-color: ${theme['darker-bg']}!important;
                      background-color: ${theme['dark-bg']}!important;
                  }
                  .Select-option,
                  .Select-noresults {
                      background-color: ${theme['darker-bg']};
                      color: #cdd3e1;
                  }
                  .Select-option.is-focused {
                      background-color: ${theme['main-tipography']};
                      color: white;
                  }
                  .Select-menu-outer {
                      border-color: theme[ 'darker-bg' ];
                  }
                  .Select-menu {
                      &::-webkit-scrollbar {
                          width: 7px;
                          background: #0d1820;
                      }
                      &::-webkit-scrollbar-track {
                          background: #0d1820;
                          border-radius: 0;
                          margin: 1px;
                      }
                      &::-webkit-scrollbar-track-piece {
                          background: #0d1820;
                      }
                      &::-webkit-scrollbar-thumb {
                          background: ${theme['dark-bg']};
                          border-radius: 0;
                          height: 50px;
                          background-clip: padding-box;
                      }
                  }
              `}
`
